import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Editor } from 'primereact/editor'
import { MultiSelect } from 'primereact/multiselect'
import { RadioButton } from 'primereact/radiobutton'
import { Accordion, AccordionTab } from 'primereact/accordion'
import { classNames } from 'primereact/utils'
import DOMPurify from 'dompurify'
import { toast } from 'react-hot-toast'
import * as Proto from '@aero-platform/shared'
import { currentUserState } from '../utils/state-atoms'
import routes from '../routing/routes'
import { protoService } from '../proto/ProtoService'
import {
  useCreateSystemMessage,
  useGetSystemMessages,
  usePreviewRecipients
} from '../api/system-message-API'
import { useGetAllRaceTeams } from '../api/raceteam-API'

// User roles that can receive messages. Admin is also always included automatically.
const USER_ROLES = [
  { label: 'Admin', value: 'admin' },
  { label: 'Resubmit-Only', value: 'resubmit-only' },
  { label: 'Engineer', value: 'aero-engineer' },
  { label: 'Methodology Specialist', value: 'methodology-specialist' },
  { label: 'Developer', value: 'developer' },
  { label: 'Dashboard-Only', value: 'dashboard-only' }
]

type MessageForm = {
  subject: string
  message: string
  roleRecipients: string[]
  teamRecipients: string[]
}

type SystemMessageItem = {
  _id?: string
  subject?: string
  content?: string
  timeCreated?: string | number
  createByUser?: string
  createByUserDisplay?: string
  roleRecipients?: string[]
  seriesRecipients?: string[]
  resolvedRecipients?: string[]
  sendSuccess?: boolean
  messageType?: string
}

const parseSystemMessageDate = (value?: string | number): Date | null => {
  if (value === undefined || value === null || value === '') return null

  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isNaN(numeric)) {
    const epochMs = numeric < 1e12 ? numeric * 1000 : numeric
    const date = new Date(epochMs)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const SystemMessages: FC = () => {
  const currentUser = useRecoilValue(currentUserState)
  const navigate = useNavigate()
  const [view, setView] = useState<'home' | 'compose' | 'review'>('home')
  const [messageData, setMessageData] = useState<MessageForm | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [audienceScope, setAudienceScope] = useState<'all' | 'targeted'>('targeted')
  const [messages, setMessages] = useState<SystemMessageItem[]>([])
  const [previewList, setPreviewList] = useState<string[]>([])
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [deselectedRecipients, setDeselectedRecipients] = useState<Set<string>>(new Set())
  const [raceTeamOptions, setRaceTeamOptions] = useState<Array<{ label: string; value: string }>>(
    []
  )

  const [, createSystemMessage] = useCreateSystemMessage()
  const [{ data: messagesData, loading: loadingMessages }, fetchMessages] = useGetSystemMessages()
  const [, fetchRaceTeams] = useGetAllRaceTeams()
  const [, fetchPreviewRecipients] = usePreviewRecipients()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<MessageForm>({
    mode: 'onChange',
    defaultValues: {
      subject: '',
      message: '',
      roleRecipients: [],
      teamRecipients: []
    }
  })

  // Restrict access to admin and developer roles only
  useEffect(() => {
    if (
      currentUser &&
      currentUser.role?.roleId !== 'admin' &&
      currentUser.role?.roleId !== 'developer'
    ) {
      navigate(routes.Dashboard.absolutePath, { replace: true })
    }
  }, [currentUser, navigate])

  useEffect(() => {
    fetchRaceTeams()
      .then(res => {
        const data = res?.data
        if (!data?.protoBytesList) {
          setRaceTeamOptions([])
          return
        }

        const teams = data.protoBytesList
          .map((item: string) => protoService.deSerialize(item, 'raceteam'))
          .filter((team: any) => Boolean(team?.raceTeamId || team?.name || team?._id))

        const options = teams
          .map((team: any) => ({
            label: team.raceTeamId || team.name || team._id,
            value: team.raceTeamId || team._id
          }))
          .filter(option => Boolean(option.value))
          .sort((a, b) => a.label.localeCompare(b.label))

        setRaceTeamOptions(options)
      })
      .catch(err => {
        console.error('Error loading race teams:', err)
        toast.error('Failed to load recipient teams')
        setRaceTeamOptions([])
      })
  }, [])

  useEffect(() => {
    if (view === 'home') {
      fetchMessages('allTeams=true&limit=100&sort={"timeCreated":-1}')
    }
  }, [view])

  useEffect(() => {
    if (!messagesData) return

    try {
      const decoded = protoService.decodeProtoResponse(
        messagesData,
        'systemmessage'
      ) as SystemMessageItem[]
      const sorted = [...(decoded || [])].sort((a, b) => {
        const dateA = parseSystemMessageDate(a?.timeCreated)?.getTime() || 0
        const dateB = parseSystemMessageDate(b?.timeCreated)?.getTime() || 0
        return dateB - dateA
      })
      setMessages(sorted)
    } catch (err) {
      console.error('Error decoding system messages:', err)
      toast.error('Failed to load system messages')
      setMessages([])
    }
  }, [messagesData])

  const onSubmit = (data: MessageForm) => {
    // Always include admin role in recipients, plus any user-selected roles
    const selectedRoles = data.roleRecipients || []
    const rolesWithAdmin = Array.from(new Set([...selectedRoles, 'admin']))

    const resolvedData: MessageForm =
      audienceScope === 'all'
        ? {
            ...data,
            message: DOMPurify.sanitize(data.message || ''),
            roleRecipients: USER_ROLES.map(r => r.value),
            teamRecipients: raceTeamOptions.map(t => t.value)
          }
        : {
            ...data,
            message: DOMPurify.sanitize(data.message || ''),
            roleRecipients: rolesWithAdmin
          }
    setMessageData(resolvedData)
    setView('review')

    // Fetch recipient preview
    setLoadingPreview(true)
    setPreviewList([])
    fetchPreviewRecipients({
      data: {
        teams: resolvedData.teamRecipients,
        roles: resolvedData.roleRecipients
      }
    })
      .then(res => {
        const recipients = res?.data?.recipients || []
        setPreviewList(recipients)
        setDeselectedRecipients(new Set())
      })
      .catch(() => {
        toast.error('Failed to load recipient preview')
      })
      .finally(() => setLoadingPreview(false))
  }

  const handleSendMessage = async () => {
    if (!messageData || isSubmitting) return

    setIsSubmitting(true)

    try {
      const senderName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim()
      const senderEmail = currentUser?.email || ''
      const senderDisplay =
        senderName && senderEmail
          ? `${senderName} (${senderEmail})`
          : senderName || senderEmail || currentUser?._id || ''

      const systemMessage = new Proto.SystemMessage({
        subject: messageData.subject,
        content: messageData.message,
        roleRecipients: messageData.roleRecipients,
        seriesRecipients: messageData.teamRecipients,
        excludedRecipients: Array.from(deselectedRecipients),
        createByUser: currentUser?._id || '',
        createByUserDisplay: senderDisplay,
        timeCreated: new Date().toISOString(),
        messageType: 'announcement',
        sendSuccess: false
      })

      const dataToPost = protoService.encodePayload(systemMessage, 'systemmessage')

      const res = await createSystemMessage({
        data: dataToPost
      })

      if (res?.data) {
        toast.success('System message sent successfully!')
        setView('home')
        setMessageData(null)
        setDeselectedRecipients(new Set())
        reset()
        fetchMessages('allTeams=true&limit=100&sort={"timeCreated":-1}')
      } else {
        toast.error('Failed to send message. Please try again.')
      }
    } catch (err) {
      console.error('Error sending system message:', err)
      toast.error('Error sending message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleComposeNew = () => {
    reset()
    setAudienceScope('targeted')
    setDeselectedRecipients(new Set())
    setView('compose')
  }

  const handleCancel = () => {
    reset()
    setAudienceScope('targeted')
    setDeselectedRecipients(new Set())
    setMessageData(null)
    setView('home')
  }

  const handleBackToCompose = () => {
    setDeselectedRecipients(new Set())
    setView('compose')
  }

  const handleRecipientToggle = (recipient: string) => {
    setDeselectedRecipients(prev => {
      const next = new Set(prev)
      if (next.has(recipient)) next.delete(recipient)
      else next.add(recipient)
      return next
    })
  }

  if (
    !currentUser ||
    (currentUser.role?.roleId !== 'admin' && currentUser.role?.roleId !== 'developer')
  ) {
    return null
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-neutral-50 dark:border-neutral-80">
        <h1 className="text-2xl font-bold text-gm-blue dark:text-light-blue">System Messages</h1>
        <Button
          label="Compose New Message"
          icon="pi pi-plus"
          onClick={handleComposeNew}
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
      </div>

      <div className="flex-1 overflow-auto p-4">
        {view === 'home' ? (
          <div className="max-w-7xl mx-auto">
            <div className="bg-white dark:bg-neutral-90 rounded-lg shadow-lg p-6">
              {loadingMessages ? (
                <p className="text-gray-600 dark:text-gray-400 text-center">Loading messages...</p>
              ) : messages.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  No messages yet. Click "Compose New Message" to send your first system message.
                </p>
              ) : (
                <Accordion multiple>
                  {messages.map((message, index) => {
                    const parsedDate = parseSystemMessageDate(message.timeCreated)
                    const formattedDate = parsedDate
                      ? parsedDate.toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Unknown date'

                    return (
                      <AccordionTab
                        key={message._id || `${message.subject}-${index}`}
                        header={
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="flex items-center gap-2">
                              <i
                                className={`pi ${
                                  message.sendSuccess
                                    ? 'pi-check-circle text-green-600'
                                    : 'pi-times-circle text-red-500'
                                }`}
                                title={message.sendSuccess ? 'Sent successfully' : 'Failed to send'}
                              />
                              <span className="font-semibold text-gray-800 dark:text-gray-200">
                                {message.subject || 'Untitled message'}
                              </span>
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-4">
                              {formattedDate}
                            </span>
                          </div>
                        }>
                        <div className="space-y-4">
                          <div className="flex flex-col">
                            <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                              Message
                            </label>
                            <div
                              className="p-3 bg-gray-50 dark:bg-neutral-80 rounded"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(message.content || '')
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col">
                              <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                                Recipient Roles
                              </label>
                              <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded">
                                {(message.roleRecipients || [])
                                  .map(roleId => {
                                    if (roleId === 'admin') return 'Admin'
                                    return USER_ROLES.find(r => r.value === roleId)?.label || roleId
                                  })
                                  .join(', ') || 'N/A'}
                              </div>
                            </div>
                            <div className="flex flex-col">
                              <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                                Recipient Teams
                              </label>
                              <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded">
                                {(message.seriesRecipients || [])
                                  .map(
                                    teamId =>
                                      raceTeamOptions.find(t => t.value === teamId)?.label || teamId
                                  )
                                  .join(', ') || 'N/A'}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col">
                            <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                              Message Type
                            </label>
                            <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded capitalize">
                              {message.messageType || 'N/A'}
                            </div>
                          </div>

                          <div className="flex flex-col">
                            <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                              Sent By
                            </label>
                            <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded">
                              {message.createByUserDisplay || message.createByUser || 'Unknown'}
                            </div>
                          </div>

                          {message.resolvedRecipients && message.resolvedRecipients.length > 0 && (
                            <div className="flex flex-col">
                              <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                                Resolved Recipients ({message.resolvedRecipients.length})
                              </label>
                              <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded max-h-48 overflow-y-auto">
                                <ul className="list-disc list-inside space-y-1 text-sm">
                                  {message.resolvedRecipients.map((recipient, i) => (
                                    <li key={i}>{recipient}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionTab>
                    )
                  })}
                </Accordion>
              )}
            </div>
          </div>
        ) : view === 'compose' ? (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-neutral-90 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-6 text-gm-blue dark:text-light-blue">
                Compose Message
              </h2>

              <form
                onSubmit={handleSubmit(onSubmit, () =>
                  toast.error('Please fill in all required fields before sending.')
                )}
                className="space-y-6">
                <div className="flex flex-col">
                  <label className="mb-2 font-medium">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="subject"
                    control={control}
                    rules={{ required: 'Subject is required' }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        placeholder="Enter message subject"
                        className={classNames({ 'p-invalid': errors.subject })}
                      />
                    )}
                  />
                  {errors.subject && (
                    <small className="text-red-500 mt-1">{errors.subject.message}</small>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 font-medium">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="message"
                    control={control}
                    rules={{ required: 'Message is required' }}
                    render={({ field }) => (
                      <Editor
                        value={field.value}
                        onTextChange={e => field.onChange(e.htmlValue)}
                        style={{ height: '320px' }}
                        className={classNames({ 'p-invalid': errors.message })}
                      />
                    )}
                  />
                  {errors.message && (
                    <small className="text-red-500 mt-1">{errors.message.message}</small>
                  )}
                  <small className="text-gray-500 mt-1">
                    Use the toolbar above to format your message (bold, italic, lists, etc.)
                  </small>
                </div>

                <div className="flex flex-col">
                  <label className="mb-2 font-medium">
                    Audience Scope <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6 mt-1">
                    <div className="flex items-center">
                      <RadioButton
                        inputId="scope-all"
                        value="all"
                        checked={audienceScope === 'all'}
                        onChange={() => setAudienceScope('all')}
                      />
                      <label htmlFor="scope-all" className="ml-2 cursor-pointer">
                        All Users
                      </label>
                    </div>
                    <div className="flex items-center">
                      <RadioButton
                        inputId="scope-targeted"
                        value="targeted"
                        checked={audienceScope === 'targeted'}
                        onChange={() => setAudienceScope('targeted')}
                      />
                      <label htmlFor="scope-targeted" className="ml-2 cursor-pointer">
                        By Role &amp; Team
                      </label>
                    </div>
                  </div>
                  {audienceScope === 'all' && (
                    <small className="text-gray-500 mt-2">
                      This message will be sent to all platform users across all roles and teams.
                    </small>
                  )}
                </div>

                {audienceScope === 'targeted' && (
                  <>
                    <div className="flex flex-col">
                      <label className="mb-2 font-medium">
                        Recipient Roles <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="roleRecipients"
                        control={control}
                        rules={
                          audienceScope === 'targeted'
                            ? {
                                required: 'At least one role must be selected',
                                validate: value =>
                                  value.length > 0 || 'At least one role must be selected'
                              }
                            : {}
                        }
                        render={({ field }) => (
                          <MultiSelect
                            value={field.value}
                            options={USER_ROLES}
                            onChange={e => field.onChange(e.value)}
                            placeholder="Select roles to receive this message"
                            display="chip"
                            className={classNames({ 'p-invalid': errors.roleRecipients })}
                          />
                        )}
                      />
                      {errors.roleRecipients && (
                        <small className="text-red-500 mt-1">{errors.roleRecipients.message}</small>
                      )}
                      <small className="text-gray-500 mt-1">
                        Admin users will always receive this message in addition to your selections.
                      </small>
                    </div>

                    <div className="flex flex-col">
                      <label className="mb-2 font-medium">
                        Recipient Teams <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="teamRecipients"
                        control={control}
                        rules={
                          audienceScope === 'targeted'
                            ? {
                                required: 'At least one team must be selected',
                                validate: value =>
                                  value.length > 0 || 'At least one team must be selected'
                              }
                            : {}
                        }
                        render={({ field }) => (
                          <MultiSelect
                            value={field.value}
                            options={raceTeamOptions}
                            onChange={e => field.onChange(e.value)}
                            placeholder="Select teams to receive this message"
                            display="chip"
                            className={classNames({ 'p-invalid': errors.teamRecipients })}
                          />
                        )}
                      />
                      {errors.teamRecipients && (
                        <small className="text-red-500 mt-1">{errors.teamRecipients.message}</small>
                      )}
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" severity="secondary" outlined onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" label="Review and Send" />
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-neutral-90 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-6 text-gm-blue dark:text-light-blue">
                Review Message
              </h2>

              {messageData && (
                <div className="space-y-6">
                  <div className="flex flex-col">
                    <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                      Subject
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded">
                      {messageData.subject}
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                      Message
                    </label>
                    <div
                      className="p-3 bg-gray-50 dark:bg-neutral-80 rounded"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(messageData.message || '')
                      }}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                      Audience
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded">
                      {audienceScope === 'all' ? (
                        <span className="font-semibold">All Users (all roles, all teams)</span>
                      ) : (
                        <>
                          <div className="mb-2">
                            <span className="font-medium">Roles: </span>
                            {messageData.roleRecipients
                              .map(roleId => {
                                if (roleId === 'admin') return 'Admin'
                                return USER_ROLES.find(r => r.value === roleId)?.label
                              })
                              .join(', ')}
                          </div>
                          <div>
                            <span className="font-medium">Teams: </span>
                            {messageData.teamRecipients
                              .map(
                                teamId =>
                                  raceTeamOptions.find(t => t.value === teamId)?.label || teamId
                              )
                              .join(', ')}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-2 font-medium text-gray-700 dark:text-gray-300">
                      Recipients (
                      {loadingPreview ? '...' : previewList.length - deselectedRecipients.size} /{' '}
                      {previewList.length})
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-neutral-80 rounded max-h-60 overflow-y-auto border border-gray-200 dark:border-neutral-70">
                      {loadingPreview ? (
                        <p className="text-sm text-gray-500">Loading recipients...</p>
                      ) : previewList.length === 0 ? (
                        <p className="text-sm text-gray-500">No matching recipients found.</p>
                      ) : (
                        <div className="space-y-2">
                          {previewList.map((recipient, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`recipient-${i}`}
                                checked={!deselectedRecipients.has(recipient)}
                                onChange={() => handleRecipientToggle(recipient)}
                                className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                              />
                              <label
                                htmlFor={`recipient-${i}`}
                                className="text-sm cursor-pointer flex-1 select-none">
                                {recipient}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {deselectedRecipients.size > 0 && (
                      <small className="text-amber-600 dark:text-amber-400 mt-2">
                        {deselectedRecipients.size} recipient(s) will be excluded from this send.
                      </small>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      severity="secondary"
                      outlined
                      onClick={handleBackToCompose}
                      disabled={isSubmitting}>
                      Back to Edit
                    </Button>
                    <Button
                      type="button"
                      label={isSubmitting ? 'Sending...' : 'Send Message'}
                      onClick={handleSendMessage}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SystemMessages
